import { Ruler } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

// Comfort Colors 1717 (inches, garment measured flat)
const TEE_ROWS = [
  ["S", "18.25", "26.63", "16.25"],
  ["M", "20.25", "28", "17.75"],
  ["L", "22", "29.38", "19"],
  ["XL", "24", "30.75", "20.5"],
  ["2XL", "26", "31.63", "21.75"],
  ["3XL", "27.75", "32.5", "23.25"],
  ["4XL", "29.75", "33.5", "24.63"],
];

// Lane Seven LS19001 (inches, garment measured flat) — official spec sheet
const HOODIE_ROWS = [
  ["XS", "20.5", "27.5"],
  ["S", "21", "28.5"],
  ["M", "23", "29.5"],
  ["L", "24.5", "30.5"],
  ["XL", "26.5", "31.5"],
  ["2XL", "27.5", "32.5"],
  ["3XL", "28.5", "33.5"],
];

// Bella+Canvas 1010 women's micro rib baby tee (inches, garment measured flat) — official spec sheet
const BABY_ROWS = [
  ["XS", "13.5", "18.5"],
  ["S", "14.25", "19"],
  ["M", "15", "19.63"],
  ["L", "16", "20.38"],
  ["XL", "17", "21.13"],
  ["2XL", "18", "21.88"],
];


// Next Level NL3910 women's relaxed (boyfriend) tee — official Next Level spec sheet
// (3910-WEBSITE-SPEC.pdf): body width 1" below armhole, body length HPS, sleeve from center back.
const WOMENS_ROWS = [
  ["XS", "17⅝", "25¾", "5⅝"],
  ["S", "18⅝", "26⅜", "5⅞"],
  ["M", "19⅝", "27", "6⅛"],
  ["L", "20⅝", "27⅝", "6⅜"],
  ["XL", "22⅛", "28¼", "6⅝"],
  ["2XL", "23⅝", "28⅞", "6⅞"],
];

export default function SizeChart({ productType, sizes = [] }) {
  const isHoodie = productType === "hoodie";
  const isBaby = productType === "baby-tee";
  const isWomens = productType === "womens-tee";
  const allRows = isHoodie ? HOODIE_ROWS : isBaby ? BABY_ROWS : isWomens ? WOMENS_ROWS : TEE_ROWS;
  const norm = (s) => s.replace("XXL", "2XL");
  const rows = sizes.length ? allRows.filter((r) => sizes.map(norm).includes(r[0])) : allRows;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="size-chart-button"
          className="inline-flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-primary underline underline-offset-4 hover:text-black transition-colors"
        >
          <Ruler size={14} /> Size chart
        </button>
      </DialogTrigger>
      <DialogContent
        className="border-2 border-black bg-background shadow-hard-lg sm:rounded-none max-w-xl"
        data-testid="size-chart-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-3xl tracking-tight">
            {isHoodie ? "Hoodie" : isBaby ? "Fitted Baby Tee" : isWomens ? "Women's Relaxed" : "Unisex Heavyweight"} size chart
          </DialogTitle>
        </DialogHeader>
        <div className="font-mono text-xs text-black/60 leading-relaxed">
          <DialogDescription className="sr-only">
            {isHoodie ? "Hoodie garment measurements in inches" : isBaby ? "Baby tee garment measurements in inches" : isWomens ? "Women's relaxed tee garment measurements in inches" : "Tee garment measurements in inches"}
          </DialogDescription>
          {isHoodie
            ? "Unisex relaxed fit — take your usual size."
            : isBaby
              ? "Fitted cut — size up for a relaxed look."
              : isWomens
                ? "Women's relaxed (boyfriend) fit — Next Level NL3910. Take your usual size."
                : "Unisex relaxed fit — take your usual size."}{" "}
          Manufacturer spec, measured flat in inches: width is chest 1" below the armhole, length is high-point shoulder to hem{isWomens ? ", sleeve from center back" : ""}.
        </div>
        <table className="w-full border-2 border-black font-mono text-sm" data-testid="size-chart-table">
          <thead>
            <tr className="bg-black text-background text-left">
              <th className="px-3 py-2 font-bold uppercase tracking-widest text-xs">Size</th>
              <th className="px-3 py-2 font-bold uppercase tracking-widest text-xs">Width</th>
              <th className="px-3 py-2 font-bold uppercase tracking-widest text-xs">Length</th>
              {!isHoodie && !isBaby && <th className="px-3 py-2 font-bold uppercase tracking-widest text-xs">Sleeve</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[0]} className="border-t-2 border-black odd:bg-[#EAEAE4]" data-testid={`size-row-${r[0]}`}>
                <td className="px-3 py-2 font-bold">{r[0]}</td>
                <td className="px-3 py-2">{r[1]}"</td>
                <td className="px-3 py-2">{r[2]}"</td>
                {!isHoodie && !isBaby && <td className="px-3 py-2">{r[3]}"</td>}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="font-mono text-[11px] text-black/60 leading-relaxed">
          {isHoodie
            ? "Heavyweight 10 oz fleece, not garment-dyed — allow up to 1\" of variation. Between sizes? Size up and call it a statement."
            : isBaby
              ? "Micro-rib knit has stretch — measured flat and relaxed, allow up to 1\" of variation. Fitted cut, so between sizes go up."
              : isWomens
                ? "Combed ring-spun cotton — allow up to 1\" of variation. Relaxed through the body; between sizes, go down for a closer fit."
                : "Garment-dyed cotton has opinions of its own — allow up to 1\u00BC\" of variation. Between sizes? Size up and call it a statement."}
        </p>
      </DialogContent>
    </Dialog>
  );
}
